import { Button, TextButton } from '@creditornot/cb-button';
import { Checkbox } from '@creditornot/cb-checkbox';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { readDeposits } from 'apis/restaurant-api/merchant-admin/deposits/api';
import { Category, Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { APIErrorNotification, WoltModal } from 'common/components';
import { CurrencyContext } from 'common/formatting/CurrencyContext';
import { localize } from 'common/utils';
import { MenuMetaContext } from 'modules/menu/MenuMetaContext';
import { toSheet, uploadToGoogleSheets } from 'modules/menu/google-sheets/googleSheets';
import { trackDownloadItemsAsCsv } from 'modules/menu/hooks/telemetryHooks';

import { getCsvSettingsData } from './CSVItemDetailsUploadModal/utils/utils';
import { GoogleModeSwitchButton } from './GoogleSheetsSelectZone';
import { csvColumnsTranslationMap } from './constants';
import { getWidths, getNumberFormats } from './gsFormats';
import { getValidations } from './gsValidations';
import useAvailableColumnKeys from './hooks/useAvailableColumnKeys';
import { useIsGoogleSheetsEnabled } from './useIsGoogleSheetsEnabled';
import { downloadTransformedDataAsCsv, mapSelectedFields } from './utils';
import { MenuLocaleContext } from '../MenuLocaleContext';
import { MenuFeaturesContext } from '../components/MenuFeaturesContext';
import { MenuPermissionsContext } from '../components/MenuPermissionsContext';

type Props = {
  isOpen: boolean;
  items: Item[];
  categories: Category[];
  language: Language;
  venueName: string;
  onDownload: () => void;
  onClose: () => void;
};

const StyledWoltModal = styled(WoltModal)`
  min-width: 480px;
  max-width: 720px;
  background: ${cbSemanticCssVars.surfaceMain};
  color: ${cbSemanticCssVars.textPrimary};
  border-radius: 8px;
`;

const StyledModalContent = styled(ModalContent)`
  height: 480px;
  overflow: auto;
`;

const ContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ContentInfo = styled.div`
  ${typographyCss.Body3};
  margin-inline-end: 16px;
`;

const StyledCheckbox = styled(Checkbox)`
  ${typographyCss.Body3};
  margin-block-start: 16px;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
`;

const CSVItemDetailsDownloadModal = ({
  isOpen,
  items,
  categories,
  language,
  venueName,
  onDownload,
  onClose,
}: Props) => {
  const isGoogleSheetsEnabled = useIsGoogleSheetsEnabled();

  const { menuId, venueId } = useContext(MenuMetaContext);
  const { customCommissionRead } = useContext(MenuPermissionsContext);
  const { country } = useContext(MenuLocaleContext);
  const { currency } = useContext(CurrencyContext);
  const { bottleDeposits } = useContext(MenuFeaturesContext);
  const [googleSheetsMode, setGoogleSheetsMode] = useState<boolean>(false);

  const { availableCsvColumns, selectedColumns, selectColumns, deselectColumns } = useAvailableColumnKeys('download');

  const { data: deposits } = useQuery(
    ['v1/merchant-admin/deposit_permutations', country],
    () => readDeposits(country),
    {
      refetchOnMount: false,
      enabled: bottleDeposits,
    },
  );

  const {
    mutateAsync: handleDownload,
    isLoading: downloading,
    error: downloadError,
  } = useMutation(async () => {
    const csvSettingsData = await getCsvSettingsData(menuId, venueId, items, selectedColumns, customCommissionRead);
    const transformedData = mapSelectedFields(
      items,
      selectedColumns,
      {
        categories,
        language,
        deposits,
        ...csvSettingsData,
      },
      googleSheetsMode,
    );
    if (googleSheetsMode) {
      // categories are already flatten here
      const categoryData = categories.map(category => ({
        category_id: !!category.child_categories.length ? '' : category.id,
        category_name: localize(category.name, language),
      }));

      const numberFormats = getNumberFormats(currency, selectedColumns);
      const validations = getValidations(selectedColumns);
      const widths = getWidths(selectedColumns);

      const spreadsheet = await uploadToGoogleSheets(
        `${venueName}-${DateTime.local().toISODate()}`,
        toSheet('items', transformedData, numberFormats, validations, widths),
        toSheet('categories', categoryData),
      );
      window.open(spreadsheet?.spreadsheetUrl, '_blank')?.focus();
    } else {
      downloadTransformedDataAsCsv(
        `${venueName}-item-details-${DateTime.local().toISODate()}.csv`,
        language,
        transformedData,
      );
    }
    onDownload();
    onClose();
  });

  const areAllFieldsSelected = selectedColumns.length === availableCsvColumns.length;

  return (
    <StyledWoltModal isOpen={isOpen} onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage
          id={isGoogleSheetsEnabled ? 'menu.csvItemExport.exportItems' : 'menu.csvItemExport.exportItemsAsCsv'}
        />
      </ModalHeader>
      {isGoogleSheetsEnabled && (
        <GoogleModeSwitchButton
          csvLabel="menu.googleSheets.asCsv"
          googleSheetsLabel="menu.googleSheets.toGoogleSheets"
          value={googleSheetsMode}
          onChange={setGoogleSheetsMode}
        />
      )}

      <StyledModalContent>
        <ContentHeader>
          <ContentInfo>
            <FormattedMessage id="menu.csvItemExport.includeFieldsDescription" />
          </ContentInfo>
          <TextButton
            variant="blue"
            onClick={() =>
              areAllFieldsSelected ? deselectColumns(selectedColumns) : selectColumns(availableCsvColumns)
            }
          >
            <FormattedMessage
              id={areAllFieldsSelected ? 'menu.csvItemExport.deselectAll' : 'menu.csvItemExport.selectAll'}
            />
          </TextButton>
        </ContentHeader>
        {availableCsvColumns.map(key => (
          <StyledCheckbox
            key={key}
            checked={selectedColumns.includes(key)}
            onChange={event => (event.currentTarget.checked ? selectColumns([key]) : deselectColumns([key]))}
          >
            <FormattedMessage id={csvColumnsTranslationMap[key]} />
          </StyledCheckbox>
        ))}
        {!!downloadError && <APIErrorNotification error={downloadError} style={{ marginBlockEnd: 16 }} />}
      </StyledModalContent>
      <StyledModalFooter>
        <Button size="small" variant="lightBlue" onClick={onClose} disabled={downloading}>
          <FormattedMessage id="state.cancel" />
        </Button>
        <Button
          style={{ marginInlineStart: 8 }}
          size="small"
          variant="blue"
          onClick={() => {
            trackDownloadItemsAsCsv(selectedColumns);
            handleDownload();
          }}
          loading={downloading}
          disabled={selectedColumns.length === 0 || downloading}
        >
          <FormattedMessage id={googleSheetsMode ? 'state.export' : 'state.download'} />
        </Button>
      </StyledModalFooter>
    </StyledWoltModal>
  );
};

export default CSVItemDetailsDownloadModal;
